import type { AvatarProps } from 'antd';
import { Avatar, theme } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';

const HASH_COLORS = [
  '#1677ff',
  '#52c41a',
  '#722ed1',
  '#fa8c16',
  '#eb2f96',
  '#13c2c2',
  '#d4b106',
  '#cf1322',
] as const;

function hashString(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = (h << 5) + h + s.charCodeAt(i);
  }
  return Math.abs(h);
}

function pickColor(seed: string): string {
  if (!seed) return HASH_COLORS[0];
  return HASH_COLORS[hashString(seed) % HASH_COLORS.length];
}

function textColorOn(background: string): string {
  const rgb = background
    .slice(1)
    .match(/.{2}/g)!
    .map((channel) => {
      const value = parseInt(channel, 16) / 255;
      return value <= 0.04045
        ? value / 12.92
        : ((value + 0.055) / 1.055) ** 2.4;
    });
  const luminance = rgb[0] * 0.2126 + rgb[1] * 0.7152 + rgb[2] * 0.0722;
  return luminance > 0.179 ? '#000000' : '#ffffff';
}

function pickInitial(name?: string, email?: string): string {
  if (name) {
    const trimmed = name.trim();
    if (trimmed) {
      const ch = Array.from(trimmed)[0];
      return /[a-zA-Z]/.test(ch) ? ch.toUpperCase() : ch;
    }
  }
  if (email) {
    const local = email.split('@')[0].trim();
    if (local) return Array.from(local)[0].toUpperCase();
  }
  return '?';
}

function resolveSize(size: AvatarProps['size']): number {
  if (typeof size === 'number') return size;
  if (size === 'small') return 24;
  if (size === 'large') return 40;
  return 32;
}

function normalizeAvatarSrc(src?: string | null): string {
  const value = typeof src === 'string' ? src.trim() : '';
  if (!value) return '';
  const lower = value.toLowerCase();
  if (lower === 'null' || lower === 'undefined') return '';
  return value;
}

export interface UserAvatarProps
  extends Omit<AvatarProps, 'src' | 'children' | 'icon'> {
  src?: string | null;
  name?: string | null;
  email?: string | null;
  subtle?: boolean;
}

const UserAvatar: React.FC<UserAvatarProps> = ({
  src,
  name,
  email,
  size,
  shape = 'circle',
  style,
  subtle,
  className,
  onError,
  ...rest
}) => {
  const { token } = theme.useToken();
  const [imageBroken, setImageBroken] = useState(false);
  const normalizedSrc = normalizeAvatarSrc(src);
  const effectiveSrc = normalizedSrc && !imageBroken ? normalizedSrc : '';
  const initial = useMemo(
    () => pickInitial(name ?? undefined, email ?? undefined),
    [name, email],
  );
  const color = useMemo(
    () => pickColor(`${name ?? ''}|${email ?? ''}`),
    [name, email],
  );
  const fontSize = Math.max(11, Math.round(resolveSize(size) * 0.45));

  const bgColor = subtle ? token.colorFillSecondary : color;
  const fgColor = subtle ? token.colorTextSecondary : textColorOn(color);
  const resolvedSize = resolveSize(size);
  const mergedStyle: React.CSSProperties = {
    backgroundColor: bgColor,
    color: fgColor,
    fontSize,
    fontWeight: subtle ? 500 : 500,
    ...style,
  };
  const shapeStyle: React.CSSProperties =
    shape === 'circle'
      ? {
          borderRadius: '50%',
          overflow: 'hidden',
        }
      : {};

  useEffect(() => {
    setImageBroken(false);
  }, [normalizedSrc]);

  if (!effectiveSrc) {
    return (
      <span
        className={`ant-avatar ant-avatar-${shape}${className ? ` ${className}` : ''}`}
        style={{
          width: resolvedSize,
          height: resolvedSize,
          lineHeight: `${resolvedSize}px`,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          userSelect: 'none',
          ...mergedStyle,
          ...shapeStyle,
        }}
        {...rest}
      >
        <span
          style={{
            lineHeight: 1,
            transform: 'none',
          }}
        >
          {initial}
        </span>
      </span>
    );
  }

  return (
    <Avatar
      size={size}
      shape={shape}
      src={effectiveSrc}
      className={className}
      style={mergedStyle}
      onError={() => {
        const shouldFallback = onError?.();
        if (shouldFallback !== false) {
          setImageBroken(true);
        }
        return false;
      }}
      {...rest}
    >
      {initial}
    </Avatar>
  );
};

export default UserAvatar;
